// MES TypeScript Types

// ==================== TIMELINE ====================
export interface Timeline {
  ID: number;
  ProductionDate: string;
  Version: number;
  Status: 'draft' | 'confirmed' | 'superseded';
  ParentTimeline?: number;
  CreatedAt: string;
  ConfirmedAt?: string;
  Notes?: string;
  /** Start of the production day (first mixing), "HH:MM". Null = global setting. */
  ShiftStartTime?: string | null;
  TimelineBatch?: TimelineBatch[];
}

/** One MES scheduling parameter (key/value), e.g. `timeline.shift_start_time`. */
export interface MesSetting {
  ID: number;
  SettingKey: string;
  SettingValue?: string | null;
  Notes?: string | null;
}

/** Keys of the scheduling settings the planning UI reads/writes. */
export const MES_SETTING_KEYS = {
  SHIFT_START: 'timeline.shift_start_time',
  GAP_BETWEEN_BATCHES: 'timeline.gap_between_batches_min',
  STARTER_REFRESH_TIME: 'timeline.starter_refresh_time',
  STARTER_REFRESH_DURATION: 'timeline.starter_refresh_duration_min',
} as const;

/** Keys of the starter preparation parameters (Plan startera). */
export const STARTER_SETTING_KEYS = {
  SAFETY_KG: 'starter.safety_kg',
  DAILY_MOTHER_KG: 'starter.daily_mother_kg',
  NIGHT_FLOUR_RATIO: 'starter.night_flour_ratio',
  NIGHT_HYDRATION: 'starter.night_hydration',
  MORNING_FLOUR_RATIO: 'starter.morning_flour_ratio',
  MORNING_HYDRATION: 'starter.morning_hydration',
  RYE_FLOUR_RATIO: 'starter.rye_flour_ratio',
  RYE_HYDRATION: 'starter.rye_hydration',
  DAILY_FLOUR_RATIO: 'starter.daily_flour_ratio',
  DAILY_HYDRATION: 'starter.daily_hydration',
  MIXER_MACHINE_ID: 'starter.mixer_machine_id',
} as const;

/** A scheduled phase segment of a batch (dynamic phase model).
 * Covers every phase the routing defines, including the ones the legacy fixed
 * columns cannot hold (dividing, moulding, cooling, packaging) and parallel
 * groups. Persisted as TimelineBatchPhase, so it survives saving a plan. */
export interface TimelinePhaseSegment {
  Code: string;
  Name?: string;
  Start?: string;
  End?: string;
  DurationMin?: number | null;
  MachineID?: number | null;
  Workers?: number | null;
  PhaseTypeID?: number | null;
  /** Routing order of the phase; segments sharing it are a parallel group. */
  Sequence?: number | null;
  /** True when the segment shares its window with another phase of the batch. */
  Parallel?: boolean;
}

export interface TimelineBatch {
  ID: number;
  Timeline: number;
  MixCode: string;
  ProductionLotCode?: string | null;
  ProductionLotAssignedAt?: string | null;
  ProductID?: number;
  ProductName?: string;
  RecipeID?: number;
  RecipeCode?: string;
  Sequence: number;
  MixerName?: string;
  ProcessName?: string;
  OvenName?: string;
  MixerMachineID?: number;
  ProcessingMachineID?: number;
  OvenMachineID?: number;
  MixingWorkers?: number;
  ProcessingWorkers?: number;
  BakingWorkers?: number;
  FinalMassKg?: number;
  MixingTimeMin?: number;
  BulkFermentationMin?: number;
  ProcessingTimeMin?: number;
  FinalProofMin?: number;
  BakingTimeMin?: number;
  MixingStart?: string;
  MixingEnd?: string;
  FermentationStart?: string;
  FermentationEnd?: string;
  ProcessingStart?: string;
  ProcessingEnd?: string;
  FinalProofStart?: string;
  FinalProofEnd?: string;
  BakingStart?: string;
  BakingEnd?: string;
  // Full ordered phase segments (dynamic phase model). Present on generated
  // batches and, since they are stored as TimelineBatchPhase, on saved ones too.
  Phases?: TimelinePhaseSegment[];
  BatchStatus: BatchStatus;
  // Day this batch is actually produced. Undefined/null = same as the
  // timeline's ProductionDate; set for staggered semi-finished products.
  ProductionDate?: string | null;
  IsSemiFinished?: boolean;
  SemiFinishedID?: number | null;
  ParentProductID?: number | null;
  // Auxiliary display markers from the timeline engine (not DB columns)
  is_semi_finished?: boolean;
  semi_finished_id?: number;
  parent_product_id?: number;
}

export type BatchStatus = 'planned' | 'ready' | 'in_progress' | 'completed' | 'on_hold' | 'skipped' | 'canceled';

/** Statuses whose batches may still be resequenced during an operational change. */
export const OPERATIONAL_ACTIVE_STATUSES: BatchStatus[] = ['planned', 'ready', 'on_hold'];

export type DispatchOverrideType = 'STATUS_CHANGE' | 'MOVE_BEFORE' | 'MOVE_AFTER' | 'DELAY' | 'CANCEL';

/** Payload for POST /api/mes/dispatch-overrides (PascalCase to match the backend). */
export interface DispatchOverridePayload {
  Timeline: number;
  TimelineBatch: number;
  OverrideType: DispatchOverrideType;
  RefBatch?: number;
  DelayMinutes?: number;
  OldStatus?: string;
  NewStatus?: string;
  Reason?: string;
}

/** A batch as returned by the production-day aggregation, with its plan day. */
export interface ProductionDayBatch extends TimelineBatch {
  /** Plan/timeline day this batch administratively belongs to. */
  PlanProductionDate?: string | null;
  TimelineStatus?: string | null;
}

export interface ProductionDayResult {
  productionDate: string;
  batches: ProductionDayBatch[];
}

// ==================== PRODUCTION WORK ORDERS ====================
// Backed by dbo."ProductionWorkOrder" (resolved via raw SQL on the backend).
export interface ProductionWorkOrder {
  ID: number;
  WorkOrderNumber?: string | null;
  SalesOrder?: number | null;
  Product?: number | null;
  Warehouse?: number | null;
  QuantityToProduce?: number | null;
  UnitType?: number | null;
  Status?: number | null;
  Priority?: number | null;
  DueDate?: string | null;
  Notes?: string | null;
  CreateDate?: string | null;
  ProductName?: string | null;
  ProductCode?: string | null;
  UnitTypeName?: string | null;
  UnitTypeCode?: string | null;
  StatusCode?: string | null;
  StatusName?: string | null;
  StatusBackgroundColor?: string | null;
  StatusBorderColor?: string | null;
  StatusTextColor?: string | null;
}

export interface ProductionWorkOrderStatus {
  ID: number;
  Code?: string | null;
  Name?: string | null;
  BackgroundColor?: string | null;
  BorderColor?: string | null;
  TextColor?: string | null;
  SortOrder?: number | null;
  IsActive?: boolean | null;
}

/** Writable columns of dbo."ProductionWorkOrder" (see MES_UpdateProductionWorkOrder). */
export interface ProductionWorkOrderPayload {
  WorkOrderNumber?: string | null;
  SalesOrder?: number | null;
  Product?: number | null;
  Warehouse?: number | null;
  QuantityToProduce?: number | null;
  UnitType?: number | null;
  Status?: number | null;
  Priority?: number | null;
  DueDate?: string | null;
  Notes?: string | null;
}

/**
 * Zamjesa koja izvršava nalog proizvodnje, s procesnim parametrima. Veza je
 * izvedena (isti proizvod + raspon datuma oko roka) jer nalog proizvodnje nema
 * kolonu prema zamjesu.
 */
export interface ProductionWorkOrderBatch extends ProductionDayBatch {
  ProcessParameter?: ProcessParameter | null;
}

// Full batch "passport" returned by GET /api/mes/batches/:id — aggregates every
// traceability link that hangs off TimelineBatch (existing tables only).
export interface BatchLotConsumptionRow {
  ID: number;
  PlannedKg?: number | null;
  ActualKg?: number | null;
  AssignedAt?: string | null;
  Source?: string | null;
  Ingredient_BatchLotConsumptionToIngredient?: { ID: number; IngredientName?: string } | null;
  MaterialLot_BatchLotConsumptionToMaterialLot?: {
    ID?: number | null;
    MaterialType?: string | null;
    LotNumberSupplier?: string | null;
    InternalLotCode?: string | null;
    Notes?: string | null;
    Supplier_MaterialLotToSupplier?: { Name?: string } | null;
  } | null;
}

export interface BatchStaffRow {
  ID: number;
  Role?: string;
  Staff_BatchStaffToStaff?: { FirstName?: string; LastName?: string; FullName?: string | null; Position?: string | null } | null;
}

export interface BatchStatusLogRow {
  ID: number;
  OldStatus?: string | null;
  NewStatus: string;
  ChangedAt: string;
  Source?: string | null;
}

export interface FinishedGoodsStockRow {
  ID: number;
  QuantityPieces: number;
  StockDate?: string | null;
  Product_FinishedGoodsStockToProduct?: { ProductName?: string } | null;
}

/** A single produced batch contributing to a product's finished-goods stock. */
export interface FinishedGoodsStockBatch {
  stock_id: number;
  batch_id: number | null;
  mix_code: string | null;
  production_lot_code: string | null;
  production_date: string | null;
  stock_date: string | null;
  quantity_pieces: number;
  final_mass_kg: number | null;
  batch_status: string | null;
}

/** Finished-goods stock aggregated per product (GET /finished-goods-stock). */
export interface FinishedGoodsStockOverview {
  product_id: number;
  product_name: string | null;
  product_code: string | null;
  unit_weight_g: number | null;
  total_pieces: number;
  /** Warehouse the finished goods are attributed to (the material warehouse). */
  warehouse_id: number | null;
  warehouse_name: string | null;
  warehouse_code: string | null;
  batches: FinishedGoodsStockBatch[];
}

// Row returned by GET /api/mes/batches (list/search) — batch joined to its timeline.
export interface BatchListItem extends TimelineBatch {
  Timeline_TimelineToTimelineBatch?: {
    ProductionDate?: string;
    Version?: number;
    Status?: string;
  };
}

export interface BatchPassport extends TimelineBatch {
  Timeline_TimelineToTimelineBatch?: Timeline;
  BatchLotConsumption?: BatchLotConsumptionRow[];
  BatchStaff?: BatchStaffRow[];
  BatchStatusLog?: BatchStatusLogRow[];
  FinishedGoodsStock?: FinishedGoodsStockRow[];
  ProcessParameter?: ProcessParameter[];
}

// ==================== PRODUCTS ====================
/**
 * Pogon koji radi proizvod — Pekara ili Slastičarna. Ista podjela kao skladišta
 * ZGPM i ZGMS.
 */
export interface ProductionUnit {
  ID: number;
  Code: string;
  Name: string;
  IsActive?: boolean;
  SortOrder?: number;
}

export interface Product {
  ID: number;
  ProductName: string;
  ProductCode?: string;
  Category?: string;
  UnitWeightG?: number;
  PiecesPerBatch?: number;
  MixingTimeMin?: number;
  BulkFermentationMin?: number;
  ProcessingTimeMin?: number;
  FinalProofMin?: number;
  BakingTemp?: number;
  BakingTime?: number;
  DefaultBatchMassKg?: number;
  IsActive?: boolean;
  CreatedAt?: string;
  MixerMachine?: number;
  ProcessingMachine?: number;
  OvenMachine?: number;
  ProductionUnit?: number | null;
  HideInTraceability?: boolean;
  Machine_MachineToProduct_MixerMachine?: Machine;
  Machine_MachineToProduct_ProcessingMachine?: Machine;
  Machine_MachineToProduct_OvenMachine?: Machine;
  ProductionUnit_ProductToProductionUnit?: ProductionUnit | null;
  ProductionPhase?: ProductionPhase[];
  Recipe?: Recipe[];
}

// ==================== RECIPES ====================
/**
 * Kako se čitaju brojevi na linijama recepture.
 * `baker_pct` — pekarski postotak brašna (PEK %), masa se izvodi iz mase zamjesa.
 * `absolute`  — slastičarska receptura: apsolutne količine po šarži (g, kom, list),
 *               koje se gledaju i pomnožene množiteljem (x5, x7.5, x10).
 */
export type RecipeQuantityBasis = 'baker_pct' | 'absolute';

export interface Recipe {
  ID: number;
  Product?: number | null;
  SemiFinished?: number | null;
  RecipeCode?: string;
  RecipeVersion?: number;
  QuantityBasis?: RecipeQuantityBasis;
  /** Množitelji uz ovu recepturu, npr. "5,7.5,10". */
  ScaleFactors?: string | null;
  IsActive?: boolean;
  ValidFrom?: string;
  ValidTo?: string;
  Notes?: string;
  CreatedAt?: string;
  Product_ProductToRecipe?: Product;
  SemiFinishedProduct?: SemiFinishedProduct;
  RecipeIngredient_RecipeToRecipeIngredient_Recipe?: RecipeIngredient[];
  RecipeParameter?: RecipeParameter[];
}

/**
 * A recipe reduced to its material lines, from `GET /recipes/material-lines`.
 *
 * The full recipe payload descends into mixer parameters and the semi-finished
 * production-phase tree, which the material planning screens never read but pay
 * for on every request. This is the shape they actually use.
 *
 * Child-recipe lines are present with a null `ingredient_id`: they carry no
 * stock material of their own, but their baker % belongs in the denominator that
 * maps percentages onto a batch mass.
 */
export interface RecipeMaterialLine {
  ingredient_id: number | null;
  child_recipe_id: number | null;
  baker_percentage: number;
  ingredient_name: string | null;
  ingredient_type_name: string | null;
}

export interface RecipeMaterialLines {
  recipe_id: number;
  recipe_code: string | null;
  recipe_version: number | null;
  lines: RecipeMaterialLine[];
}

/**
 * A mixer PLC setpoint stored against a recipe. `GET /recipes/:id/parameters`
 * always returns the full canonical set, so a parameter nobody has entered comes
 * back with a null `ParamValue`, a null `ID` and `IsSaved` false. The recipe
 * detail payload, by contrast, carries only the rows that exist.
 */
export interface RecipeParameter {
  ID: number | null;
  Recipe: number;
  ParamKey: string;
  ParamValue: number | null;
  Unit?: string;
  SortOrder?: number;
  Notes?: string | null;
  IsSaved?: boolean;
}

/** A null value clears the parameter rather than storing a zero. */
export interface RecipeParameterPayload {
  ParamKey: string;
  ParamValue: number | null;
  Notes?: string | null;
}

export interface RecipeIngredient {
  ID: number;
  Recipe: number;
  Ingredient?: number | null;
  ChildRecipe?: number | null;
  /** Samo kod QuantityBasis 'baker_pct'; kod 'absolute' je null. */
  BakerPercentage?: number | null;
  /** Samo kod QuantityBasis 'absolute': apsolutna količina po jednoj šarži. */
  Quantity?: number | null;
  UnitType?: number | null;
  IsAdjustable?: boolean;
  SortOrder?: number;
  Notes?: string;
  Ingredient_IngredientToRecipeIngredient?: Ingredient;
  UnitType_RecipeIngredientToUnitType?: UnitType | null;
  Recipe_RecipeToRecipeIngredient_ChildRecipe?: Recipe;
}

export type SemiFinishedType = 'starter' | 'enzyme' | 'seed_mix' | 'other';

export interface SemiFinishedProduct {
  ID: number;
  Name: string;
  SemiFinishedType: SemiFinishedType;
  Ingredient?: number | null;
  MixingTimeMin?: number;
  BulkFermentationMin?: number;
  ProcessingTimeMin?: number;
  FinalProofMin?: number;
  BakingTime?: number;
  BakingTemp?: number;
  IsActive?: boolean;
  Notes?: string;
  CreatedAt?: string;
  // Scheduling rule: when this semi-finished is produced relative to the
  // finished product that consumes it.
  ScheduleMode?: 'same_day' | 'offset_days' | 'weekday' | null;
  ProductionOffsetDays?: number | null;
  ProductionWeekday?: number | null; // 0=Sunday .. 6=Saturday
  ProductionUnit?: number | null;
  ProductionUnit_SemiFinishedToProductionUnit?: ProductionUnit | null;
  ProductionPhase?: ProductionPhase[];
  recipe?: Recipe | null;
  line_count?: number;
}

export interface RecipeLinePayload {
  LineType: 'ingredient' | 'semi_finished';
  Ingredient?: number;
  ChildRecipe?: number;
  /** Pekarska receptura. Kod slastičarske se ne šalje. */
  BakerPercentage?: number | null;
  /** Slastičarska receptura: apsolutna količina + jedinica. */
  Quantity?: number | null;
  UnitType?: number | null;
  SortOrder?: number;
  Notes?: string | null;
  IsAdjustable?: boolean;
}

// ==================== INGREDIENTS ====================
export interface IngredientType {
  ID: number;
  Code?: string | null;
  Name: string;
  IsActive?: boolean;
  SortOrder?: number;
}

export interface Ingredient {
  ID: number;
  IngredientName: string;
  StandardCode?: string | null;
  RitamCode?: string | null;
  RemarisCode?: string | null;
  IngredientType?: number | null;
  UnitType?: number | null;
  IsPerishable?: boolean;
  IsActive?: boolean;
  CreatedAt?: string;
  /** Denormalized lookup values added by serializeIngredient */
  IngredientTypeName?: string | null;
  Unit?: string | null;
  IngredientType_IngredientToIngredientType?: IngredientType | null;
  UnitType_IngredientToUnitType?: { Code?: string; Name?: string } | null;
  IngredientPrice?: IngredientPrice[];
  /** Ritam aliases linked to this sirovina (Ingredient_IngredientAlias). */
  Aliases?: IngredientAlias[];
}

/** Payload for creating a sirovina, optionally linked to Ritam aliases at once. */
export type IngredientCreatePayload = Partial<Ingredient> & { AliasIDs?: number[] };

/** Alias / external-system (e.g. Ritam ERP) name for an ingredient. */
export interface IngredientAlias {
  ID: number;
  AliasName: string;
  ExternalCode?: string | null;
  SourceSystem?: string | null;
  /** Primary ingredient — the one pre-filled when this alias is picked. */
  Ingredient?: number | null;
  /** Every ingredient linked to this alias; one Ritam article can serve several. */
  Ingredients?: number[];
}

export interface IngredientPrice {
  ID: number;
  Ingredient: number;
  PricePerKg: number;
  ValidFrom?: string;
  ValidTo?: string;
  Supplier?: string;
  Notes?: string;
  CreatedAt?: string;
  IngredientName?: string;
}

// ==================== PROCUREMENT: ORDER REQUESTS (Nabava) ====================
export interface OrderRequestStatus {
  ID: number;
  Code: string;
  Name: string;
  SortOrder?: number;
  IsActive?: boolean;
}

export interface UnitType {
  ID: number;
  Code: string;
  Name: string;
  IsActive?: boolean;
  SortOrder?: number;
}

export interface OrderRequestLine {
  ID: number;
  OrderRequestID: number;
  Ingredient?: number | null;
  Supplier?: number | null;
  NewProduct?: string | null;
  Link?: string | null;
  Quantity?: number | string | null;
  UnitType?: number | null;
  Notes?: string | null;
  /** Resolved relations added by the backend */
  IngredientInfo?: Ingredient | null;
  SupplierInfo?: { ID: number; Name: string } | null;
  UnitTypeInfo?: UnitType | null;
}

export interface OrderRequest {
  ID: number;
  RequestNumber: string;
  Title?: string | null;
  Status?: number | null;
  RequestDate?: string | null;
  RequiredDeliveryDate?: string | null;
  SubmittedBy?: number | null;
  ApprovedBy?: number | null;
  ApprovedAt?: string | null;
  Priority?: string | null;
  Warehouse?: number | null;
  PurchaseOrder?: number | null;
  Notes?: string | null;
  /** Derived/related fields added by the backend */
  LineCount?: number;
  StatusInfo?: OrderRequestStatus | null;
  WarehouseInfo?: { ID: number; Name?: string } | null;
  Lines?: OrderRequestLine[];
}

export interface OrderRequestLinePayload {
  Ingredient?: number | null;
  Supplier?: number | null;
  NewProduct?: string | null;
  Link?: string | null;
  Quantity?: number | string | null;
  UnitType?: number | null;
  Notes?: string | null;
}

export interface OrderRequestPayload {
  Title?: string | null;
  Status?: number | null;
  RequestDate?: string | null;
  RequiredDeliveryDate: string;
  Priority?: string | null;
  Warehouse?: number | null;
  Notes?: string | null;
  Lines: OrderRequestLinePayload[];
}

// ==================== MACHINES ====================
export interface Machine {
  ID: number;
  MachineName: string;
  MachineType: string;
  CapacityMaxKg?: number;
  CapacityUnit?: string;
  Notes?: string;
  IsActive?: boolean;
  /** Optional link to a live IoT device (Diosna, Escher, PLC…). */
  DeviceID?: number | null;
  Device?: { ID: number; Name: string; DeviceType?: number; Status?: number; IsHost?: number } | null;
}

// ==================== PRODUCTION PHASES ====================
export interface ProductionPhaseType {
  ID: number;
  Code?: string | null;
  Name?: string | null;
  DefaultSequence?: number | null;
  UsesMachine?: boolean | null;
  SortOrder?: number | null;
  IsActive?: boolean | null;
}

/**
 * Jedan korak tehnoloskog postupka proizvoda.
 *
 * Dvije faze s ISTIM `Sequence` cine paralelnu grupu: izvode se istovremeno i
 * dijele samo trajanje (stroj, radnici, temperatura i biljeske ostaju po fazi).
 */
export interface ProductionPhase {
  ID: number;
  PhaseType?: number | null;
  Sequence?: number | null;
  DurationMin?: number | string | null;
  Machine?: number | null;
  Workers?: number | null;
  TempC?: number | string | null;
  Product?: number | null;
  SemiFinishedProduct?: number | null;
  Notes?: string | null;
  ProductionPhaseType_ProductionPhaseToPhaseType?: ProductionPhaseType | null;
  Machine_ProductionPhaseToMachine?: Machine | null;
}

/** Editor row payload sent to PUT /products|semi-finished/:id/phases */
export interface ProductionPhasePayload {
  phase_type_id?: number | null;
  sequence?: number | null;
  duration_min?: number | null;
  machine_id?: number | null;
  workers?: number | null;
  temp_c?: number | null;
  notes?: string | null;
}

// ==================== PHASE MATERIALS (dodatne sirovine po fazi) ====================
export type PhaseMaterialBasis = "per_piece" | "per_batch" | "per_kg_dough" | "baker_pct";

/** Dodatni materijal (sirovina/repro/ambalaža) koji proizvod troši u nekoj fazi. */
export interface ProductionPhaseMaterial {
  ID: number;
  Product: number;
  PhaseType: number;
  IngredientAlias: number;
  Quantity: number | string;
  UnitType?: number | null;
  QuantityBasis: PhaseMaterialBasis;
  ScrapPct?: number | string | null;
  IsOptional?: boolean | null;
  SortOrder?: number | null;
  Notes?: string | null;
  IngredientAlias_rel?: (IngredientAlias & { Ingredient_IngredientToIngredientAlias?: Ingredient | null }) | null;
  UnitType_rel?: UnitType | null;
  ProductionPhaseType_rel?: ProductionPhaseType | null;
}

export interface ProductionPhaseMaterialPayload {
  Product: number;
  PhaseType: number;
  IngredientAlias: number;
  Quantity: number;
  UnitType?: number | null;
  QuantityBasis?: PhaseMaterialBasis;
  ScrapPct?: number | null;
  IsOptional?: boolean | null;
  SortOrder?: number | null;
  Notes?: string | null;
}

/** Izvršenje: predloženi (FIFO) ili potvrđeni utrošak faznog materijala po šarži. */
export interface PhaseMaterialConsumptionRow {
  ID: number;
  phaseType: string | null;
  phaseTypeCode: string | null;
  ingredientAliasId: number | null;
  material: string;
  materialLotId: number | null;
  lotNumber: string | null;
  expiryDate?: string | null;
  plannedQty: number | null;
  actualQty: number | null;
  qty: number;
  unitPrice: number;
  cost: number;
  confirmed: boolean;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
}

/** Planirana potreba faznog materijala (normativ) — prije/bez povlačenja lota. */
export interface PhaseMaterialDemandRow {
  phaseMaterialId: number;
  phaseTypeId: number;
  phaseTypeName: string;
  phaseTypeCode: string | null;
  ingredientAliasId: number;
  ingredientId: number | null;
  materialName: string;
  quantityBasis: string;
  requiredQty: number;
  /** Jedinica iz normativa (kg, kom, …) — fazni materijal nije nužno u kilogramima. */
  unitCode?: string | null;
}

/** Potreba faznog materijala uz šaržu na koju se odnosi (skupni dohvat). */
export interface BatchPhaseMaterialDemandRow extends PhaseMaterialDemandRow {
  batchId: number;
}

/** Lot sa zalihom na izvornom skladištu — za ručni odabir u međuskladišnici. */
export interface TransferAvailableLot {
  material_lot_id: number;
  ingredient_id: number | null;
  ingredient_alias_id: number | null;
  material_name: string;
  lot_number: string | null;
  remaining: number;
  expiry_date?: string | null;
  unit_price: number;
}

/** Kandidat-lot za zamjenu predloženog lota (isti materijal). */
export interface CandidateLot {
  materialLotId: number;
  lotNumber: string | null;
  expiryDate?: string | null;
  unitPrice: number;
  remaining: number;
}

// ==================== PROCESS PARAMETERS ====================
export interface ProcessParameter {
  ID: number;
  TimelineBatch?: number;
  Product?: number;
  MixCode: string;
  ProductionDate?: string;
  ProductName?: string;
  RecipeCode?: string;
  Recipe?: number;
  MixingStart?: string;
  MixingEnd?: string;
  BulkFermentationStart?: string;
  DoughMassKg?: number;
  CapacityKg?: number;
  FillPct?: number;
  DoughTempC?: number;
  StarterPh?: number;
  EnergyPeakWh?: number;
  EnergyEndWh?: number;
  BulkFermentationPh1?: number;
  BulkFermentationPh2?: number;
  BulkFermentationDeltaPh?: number;
  BulkFermentationHeight1?: number;
  BulkFermentationHeight2?: number;
  BulkFermentationDeltaHeightPct?: number;
  BulkFermentationEnd?: string;
  FinalProofHeight1?: number;
  FinalProofHeight2?: number;
  FinalProofDeltaHeightPct?: number;
  FermentationType?: string;
  FinalProofStart?: string;
  FinalProofEnd?: string;
  PrebakePh?: number;
  BakingStart?: string;
  BakingEnd?: string;
  BakingTempC?: number;
  CoolingStart?: string;
  SpecificVolumeLkg?: number;
  PackagingStart?: string;
  PackagingEnd?: string;
  PackedPieces?: number;
  Scrap?: number;
  ScrapColor?: number;
  ScrapShape?: number;
  ProducedPieces?: number;
  ScrapPct?: number;
  ScrapColorPct?: number;
  ScrapShapePct?: number;
  Notes?: string;
  Product_ProcessParameterToProduct?: Product;
  Recipe_ProcessParameterToRecipe?: Recipe;
}

// ==================== ODSTUPANJA SIROVINA (plan vs. stvarno) ====================
/**
 * Odstupanje jedne sirovine u zamjesu: koliko je receptura tražila i koliko je
 * stvarno izvagano. Najviše jedan red po (zamjes, sirovina) — ponovno spremanje
 * prepisuje, a odstupanje vraćeno na nulu bez razloga briše red.
 */
export interface BatchAdjustment {
  adjustment_id: number;
  batch_id: number | null;
  mix_code?: string | null;
  ingredient_id: number | null;
  ingredient_name?: string | null;
  flour_lot_id?: number | null;
  planned_qty_kg: number;
  actual_qty_kg: number;
  deviation_kg: number;
  deviation_pct: number;
  reason?: string | null;
  entered_by?: string | null;
  entered_at?: string | null;
}

/** Zapis odstupanja koji ekran šalje; postotak se računa na serveru. */
export interface BatchAdjustmentInput {
  batch_id: number;
  ingredient_id: number;
  planned_qty_kg: number;
  actual_qty_kg: number;
  deviation_kg: number;
  reason?: string | null;
  flour_lot_id?: number | null;
  entered_by?: string | null;
}

// ==================== SIROVINE ZAMJESA (recept + poluproizvodi) ====================
/**
 * Evidentirana sirovina jedne zamjese (BatchLotConsumption bez faznog
 * normativa): dodijeljeni lot, količina i status potvrde utroška.
 */
export interface BatchMaterialRow {
  ID: number;
  /** `ing:<id>` / `sf:<id>` — veže red na recepturnu stavku. */
  key: string;
  kind: 'ingredient' | 'semi_finished';
  ingredientId: number | null;
  material: string;
  materialType: string | null;
  materialLotId: number | null;
  lotNumber: string | null;
  supplier: string | null;
  expiryDate?: string | null;
  plannedQty: number | null;
  actualQty: number | null;
  qty: number;
  /** Potreba po recepturi (null = stavke nema u recepturi). */
  recipeQty: number | null;
  offRecipe: boolean;
  /** Poluproizvodni lot dolazi iz vlastite zamjese i ne bira se ručno. */
  editableLot: boolean;
  unitPrice: number;
  cost: number;
  confirmed: boolean;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
  source: string;
  notes: string | null;
}

/** Recepturna stavka zamjesa koja još nema evidentiran lot. */
export interface BatchMaterialMissingRow {
  key: string;
  kind: 'ingredient' | 'semi_finished';
  ingredientId: number | null;
  semiFinishedId: number | null;
  material: string;
  materialType: string | null;
  recipeQty: number;
  /** Voda i poluproizvodi se ne vode po lotu. */
  tracksLots: boolean;
}

export interface BatchMaterialsResult {
  batchId: number;
  mixCode: string;
  doughMassKg: number | null;
  rows: BatchMaterialRow[];
  missing: BatchMaterialMissingRow[];
}

/** Kandidat-lot jedne sirovine sa stanjem na skladištu proizvodnje. */
export interface IngredientCandidateLot {
  materialLotId: number;
  lotNumber: string | null;
  supplier: string | null;
  expiryDate?: string | null;
  unitPrice: number;
  remaining: number;
}

/** Izmjena/dodavanje sirovine zamjesa na licu mjesta. */
export interface BatchMaterialPayload {
  ingredientId?: number | null;
  materialLotId?: number | null;
  qty?: number | null;
  notes?: string | null;
  changedBy?: string | null;
}

/** Dodijeljeni lot po zamjesu — ulazi u ispis radnog naloga za miksanje. */
export interface BatchMaterialLotRow {
  batchId: number;
  consumptionId: number;
  ingredientId: number | null;
  /** Set only when `include_phase_materials` was requested. */
  ingredientAliasId?: number | null;
  /** Normativni red faze — razlikuje fazni materijal od recepturne sirovine. */
  sourcePhaseMaterialId?: number | null;
  material: string | null;
  materialLotId: number | null;
  lotNumber: string | null;
  supplier: string | null;
  expiryDate?: string | null;
  isSemiFinished: boolean;
  plannedQty: number | null;
  actualQty: number | null;
  confirmed: boolean;
}

// ==================== FLOUR ====================
export interface FlourLot {
  ID: number;
  SupplierName?: string;
  FlourType?: string;
  LotNumber?: string;
  ArrivalDate?: string;
  QuantityKg?: number;
  RemainingKg?: number;
  IsActive?: boolean;
  Notes?: string;
  Ingredient?: number;
  FlourAnalysis?: FlourAnalysis[];
}

export interface FlourAnalysis {
  ID: number;
  FlourLot?: number;
  AnalysisDate?: string;
  GlutenPct?: number;
  GlutenIndex?: number;
  MoisturePct?: number;
  AshPct?: number;
  FallingNumber?: number;
  // Farinograph
  FarinWaterAbs?: number;
  FarinDevTime?: number;
  FarinStability?: number;
  FarinResistance?: number;
  FarinSoftening?: number;
  FarinQualityNumber?: number;
  FarinQualityGroup?: number;
  // Alveograph
  AlveographW?: number;
  AlveographPl?: number;
  // Extensograph 45'
  ExtResistance45?: number;
  ExtExtensib45?: number;
  ExtRatio45?: number;
  ExtEnergy45?: number;
  ExtMaxResistance45?: number;
  // Extensograph 90'
  ExtResistance90?: number;
  ExtExtensib90?: number;
  ExtRatio90?: number;
  ExtEnergy90?: number;
  ExtMaxResistance90?: number;
  // Extensograph 135'
  ExtResistance135?: number;
  ExtExtensib135?: number;
  ExtRatio135?: number;
  ExtEnergy1355?: number; // Note: typo in backend schema
  ExtMaxResistance135?: number;
  // Other
  LabName?: string;
  CertificateUrl?: string;
  Notes?: string;
}

// ==================== TRIALS ====================
export interface Trial {
  ID: number;
  TrialCode?: string;
  Recipe?: number;
  Product?: number;
  TrialDate: string;
  DoughMassKg?: number;
  Notes?: string;
  CreateDate?: string;
  Status: 'draft' | 'confirmed' | 'promoted' | 'in_progress' | 'completed' | 'archived';
  Purpose?: string;
  ConfirmedAt?: string;
  ConfirmedBy?: string;
  ProductNameCustom?: string;
  DoneAt?: string;
  NeedsReview?: boolean;
  Product_ProductToTrial?: Product;
  Recipe_RecipeToTrial?: Recipe;
  TrialIngredient?: TrialIngredient[];
  TrialParameter?: TrialParameter[];
}

export interface TrialIngredient {
  ID: number;
  Trial: number;
  Ingredient?: number;
  IngredientName: string;
  IngredientType?: string;
  FlourLot?: number;
  BakerPercentage?: number;
  MassKg?: number;
  SortOrder?: number;
  IsNewIngredient?: boolean;
  Notes?: string;
  ActualKg?: number;
  ActualNotes?: string;
  Ingredient_IngredientToTrialIngredient?: Ingredient;
}

export interface TrialParameter {
  ID: number;
  Trial: number;
  DoughMassKg?: number;
  DoughTempC?: number;
  StarterPh?: number;
  Notes?: string;
  CreateDate?: string;
  ModifyDate?: string;
}

export interface TrialSchedule {
  ID: number;
  Trial: number;
  PlannedDate: string;
  PlannedOrder: number;
  Notes?: string;
  Status: string;
  CreateDate?: string;
  Trial_TrialToTrialSchedule?: Trial;
}

// ==================== DASHBOARD ====================
export interface DashboardStatus {
  date: string;
  hasTimeline: boolean;
  timelineId?: number;
  timelineStatus?: string;
  totalBatches: number;
  completed: number;
  in_progress: number;
  planned: number;
  on_hold: number;
  skipped: number;
  canceled: number;
  ready: number;
  batches: TimelineBatch[];
}

// ==================== KPI ====================
export interface GanttData {
  timeline_id: number;
  production_date: string;
  batches: TimelineBatch[];
}

export interface MachineEfficiency {
  mixer: string;
  totalBatches: number;
  completedBatches: number;
  efficiency: string;
  totalMassKg: number;
}

export interface QualityTrend {
  MixCode: string;
  ProductionDate: string;
  ProductName?: string;
  DoughTempC?: number;
  StarterPh?: number;
  DoughMassKg?: number;
  ScrapPct?: number;
}

// ==================== DAILY PLAN ====================
export interface DailyPlanEntry {
  product_id: number;
  product_name: string;
  batch_count: number;
  mass_kg?: number;
  // Devices (predefined from product, overridable per row).
  mixer_machine_id?: number;
  processing_machine_id?: number;
  oven_machine_id?: number;
  // Workers per machine-bound phase (predefined from product machine config,
  // overridable per row).
  mixing_workers?: number;
  processing_workers?: number;
  baking_workers?: number;
}

// Active product machine config row (default workers/capacity per product+machine).
export interface ProductMachineConfig {
  ID: number;
  Product: number;
  Machine: number;
  Workers: number;
  CapacityValue?: number;
  ValidFrom?: string;
  ValidTo?: string | null;
  Notes?: string | null;
}

export type TimelineModificationAction = 'PRIORITET' | 'NOĆ' | 'CIKCAK';

export interface TimelineModification {
  Radnja: TimelineModificationAction;
  Proizvod: string;
  Mikser: string;
  Broj_zamjesa: string | number;
  Pozicija: number;
  Od_zamjesa?: number;
}

// ==================== FILTERS ====================
export interface TimelineFilters {
  status?: string;
  fromDate?: string;
  toDate?: string;
}

export interface ProductFilters {
  is_active?: boolean;
  category?: string;
  search?: string;
  /** Ucitaj i ProductionPhase retke (trajanja faza). Kosta jedan upit vise. */
  with_phases?: boolean;
  /** ID pogona (ProductionUnit) — Pekara ili Slasticarna. */
  production_unit?: number;
}

export interface TrialFilters {
  status?: string;
  product_id?: number;
  fromDate?: string;
  toDate?: string;
}

export interface DateRange {
  fromDate: string;
  toDate: string;
}

// ==================== INGREDIENT LOTS & TRACEABILITY ====================
/**
 * Stock of one lot at one warehouse, as returned with `stock_scope=production`.
 * `priority` 0 is the warehouse to take from first (the bakery's own material
 * warehouse); higher values need an inter-warehouse transfer first.
 */
export interface LotWarehouseStock {
  warehouse_id: number;
  warehouse_code: string;
  warehouse_name: string;
  remaining_kg: number;
  /** Absent for warehouses outside the production pair — they have no ranking. */
  priority?: number;
}

export interface IngredientLot {
  lot_id: number;
  ingredient_id: number | null;
  /** Set for packaging / repro lots, which have no recipe ingredient behind them. */
  ingredient_alias_id?: number | null;
  ingredient_alias_name?: string | null;
  supplier?: string;
  lot_number: string;
  delivery_date: string;
  expiry_date?: string;
  quantity_kg: number;
  remaining_kg: number;
  price_per_kg?: number;
  is_active?: boolean;
  notes?: string;
  created_at?: string;
  zboogie_ingredients?: Ingredient;
  zboogie_batch_lot_usage?: BatchLotUsage[];
  /** Only present when the lots were requested with `stock_scope: "production"`. */
  stock_by_warehouse?: LotWarehouseStock[];
  /** Stock sitting outside the bakery/central pair, e.g. at the pastry shop. */
  stock_elsewhere?: LotWarehouseStock[];
  /**
   * Whether any movement was ever booked for the lot. Without one there is no
   * location to report — the stock record is missing, not the stock.
   */
  has_stock_record?: boolean;
}

export interface GoodsReceiptLot {
  lot_id: number;
  ingredient_id: number;
  supplier?: string;
  lot_number?: string;
  delivery_date?: string;
  expiry_date?: string;
  quantity_kg: number;
  remaining_kg: number;
  status: string;
  price_per_kg?: number;
  lot_label_image_url?: string;
  zboogie_ingredients?: Ingredient;
  ritam_alias_id?: number | null;
  ritam_alias_name?: string | null;
}

export interface GoodsReceipt {
  delivery_receipt_id: number;
  delivery_note_number: string;
  supplier_name?: string;
  supplier_id?: number;
  supplier_oib?: string;
  received_date: string;
  warehouse_id?: number;
  warehouse_name?: string;
  notes?: string;
  delivery_note_image_url?: string;
  created_at?: string;
  lots: GoodsReceiptLot[];
  quarantine_count: number;
}

export interface Warehouse {
  warehouse_id: number;
  code: string;
  name: string;
  type: string;
  is_active?: boolean;
  sort_order?: number;
  /** Warehouse a goods receipt books to unless another one is picked. */
  is_default_receipt?: boolean;
  /** Warehouse an inter-warehouse transfer moves stock out of by default. */
  is_default_transfer_source?: boolean;
  /** Warehouse an inter-warehouse transfer moves stock into by default. */
  is_default_transfer_target?: boolean;
}

/** `Warehouse.type` of the warehouses that hold raw material. */
export const WAREHOUSE_TYPE_MATERIAL = "materijalno";

export interface WarehouseStockItem {
  ingredient_id: number;
  ingredient_name: string;
  ingredient_type: string | null;
  ukupno_kg: number;
  low_stock_warning?: boolean;
  lots: Array<{
    lot_id: number;
    lot_number: string | null;
    kg: number;
    expiry_date: string | null;
    status: string;
  }>;
}

export interface WarehouseStockOverview {
  warehouse_id: number;
  code: string;
  name: string;
  type: string;
  items: WarehouseStockItem[];
  total_items: number;
}

// ==================== INVENTURA ====================

/** Šifre iz InventoryCountStatus; closed = proknjižena. */
export type InventoryCountStatus =
  | 'planned'
  | 'counting'
  | 'reconciling'
  | 'closed'
  | 'cancelled';

export interface InventoryCount {
  inventory_count_id: number;
  count_number: string | null;
  count_date: string | null;
  warehouse_id: number | null;
  warehouse_name: string | null;
  warehouse_code: string | null;
  status: InventoryCountStatus | null;
  status_name: string | null;
  /** Popis se smije uređivati samo dok nije proknjižen ili otkazan. */
  is_editable: boolean;
  type: string | null;
  type_name: string | null;
  is_initial: boolean;
  notes: string | null;
  counted_by: number | null;
  reconciled_by: number | null;
  posted_at: string | null;
  total_lines: number;
  counted_lines: number;
  surplus_kg: number;
  shortage_kg: number;
  /** Vrijednost razlike po nabavnoj cijeni; negativno = manjak. */
  difference_value: number;
}

export interface InventoryCountLookup {
  id: number;
  code: string | null;
  name: string | null;
}

export interface InventoryCountLookups {
  statuses: InventoryCountLookup[];
  types: InventoryCountLookup[];
  /** DiscrepancyReason — dijeli se s primkama. */
  reasons: InventoryCountLookup[];
}

export interface InventoryCountLine {
  line_id: number;
  material_lot_id: number | null;
  ingredient_id: number | null;
  ingredient_alias_id: number | null;
  product_id: number | null;
  material_name: string | null;
  material_type: string | null;
  lot_number: string | null;
  expiry_date: string | null;
  /** Knjigovodstveno stanje u trenutku otvaranja popisa. */
  book_quantity_kg: number;
  /** null = stavka još nije prebrojana; 0 je valjan rezultat. */
  counted_quantity_kg: number | null;
  difference_kg: number | null;
  unit_price: number | null;
  /** DiscrepancyReason — obrazloženje razlike. */
  reason_id: number | null;
  is_new_lot: boolean;
  notes: string | null;
}

export interface InventoryCountDetail extends InventoryCount {
  lines: InventoryCountLine[];
}

export interface CountableItem {
  ingredient_id: number | null;
  ingredient_alias_id: number | null;
  material_name: string;
  material_type: string | null;
}

export type InterWarehouseTransferStatus =
  | 'draft'
  | 'ready'
  | 'in_transit'
  | 'completed'
  | 'cancelled';

export interface InterWarehouseTransferLine {
  line_id?: number;
  ingredient_id: number;
  ingredient_name?: string;
  material_lot_id?: number;
  lot_number?: string;
  quantity_kg: number;
  required_quantity_kg?: number;
  expiry_date?: string;
  batch_refs?: string;
  notes?: string;
  remaining_kg?: number;
  transferred?: boolean;
}

export interface InterWarehouseTransfer {
  transfer_id: number;
  transfer_number: string;
  production_date: string;
  timeline_id?: number;
  status: InterWarehouseTransferStatus;
  from_location?: string;
  to_location?: string;
  from_warehouse_id?: number;
  to_warehouse_id?: number;
  from_warehouse_name?: string;
  to_warehouse_name?: string;
  notes?: string;
  created_at?: string;
  completed_at?: string;
  lines_count?: number;
  lines?: InterWarehouseTransferLine[];
  timeline?: {
    timeline_id: number;
    production_date: string;
    status: string;
    version: number;
  };
}

export interface DeliveryLineDraft {
  key: string;
  ingredient_id: number;
  ritam_alias_id?: number;
  extracted_name?: string;
  lot_number: string;
  quantity_kg: string;
  expiry_date: string;
  price_per_kg: string;
  vat_percent: string;
  discount_percent: string;
  price_with_vat: string;
  napomena: string;
  lot_label_image_base64?: string;
  ingredient_matches?: { ingredient_id: number; ingredient_name: string; score: number }[];
  match_confidence?: number;
  needs_manual_ingredient?: boolean;
}

export interface BatchLotUsage {
  usage_id: number;
  batch_id: number;
  lot_id: number;
  ingredient_id: number;
  quantity_used_kg: number;
  notes?: string;
  created_at?: string;
  zboogie_timeline_batches?: TimelineBatch;
  zboogie_ingredient_lots?: IngredientLot;
  zboogie_ingredients?: Ingredient;
}

// ==================== TRACEABILITY (SLJEDIVOST) ====================
export interface TraceMaterialLot {
  lot_id: number;
  internal_lot_code?: string | null;
  supplier_lot?: string | null;
  ingredient_name?: string | null;
  supplier_name?: string | null;
  arrival_date?: string | null;
  quantity_kg?: number | null;
  status?: string | null;
  usage_count?: number;
}

export interface TraceDispatch {
  dispatch_id: number | null;
  dispatch_number: string | null;
  dispatch_date: string | null;
  customer_id: number | null;
  customer_name: string | null;
  warehouse_id?: number | null;
  lot_code: string | null;
  quantity: number | null;
}

export interface TraceChainReceipt {
  id: number;
  number?: string | null;
  date?: string | null;
  supplier_name?: string | null;
  warehouse?: string | null;
}

export interface TraceChainTransfer {
  id: number;
  number?: string | null;
  date?: string | null;
  from_warehouse?: string | null;
  to_warehouse?: string | null;
  from_warehouse_id?: number | null;
  to_warehouse_id?: number | null;
  from_code?: string | null;
  to_code?: string | null;
  purpose?: "writeoff" | "finished_goods" | "other" | string | null;
  quantity_kg?: number | null;
  quantity_pieces?: number | null;
}

export interface TraceQaItem {
  lot_id: number;
  ingredient_name?: string | null;
  approved_at?: string | null;
  source?: "qc" | "released" | "assumed" | "reject" | string;
}

export interface TraceQa {
  all_passed: boolean;
  items: TraceQaItem[];
}

export interface FinishedProductCheck {
  id: number;
  batch_id?: number | null;
  product_id?: number | null;
  control_date?: string | null;
  product_code?: string | null;
  product_name?: string | null;
  defined_weight_kg?: number | null;
  production_date?: string | null;
  production_department?: string | null;
  smell_ok?: boolean | null;
  taste_ok?: boolean | null;
  appearance_ok?: boolean | null;
  weight_kg: Array<number | null>;
  length_cm?: number | null;
  width_cm?: number | null;
  height_cm?: number | null;
  conforms?: boolean | null;
  label_check_ok?: boolean | null;
  controlled_by?: string | null;
  nonconformity_number?: string | null;
  notes?: string | null;
}

export interface TraceProductQa {
  all_passed: boolean;
  recorded: boolean;
  latest?: FinishedProductCheck | null;
  checks: FinishedProductCheck[];
}

export interface TraceDestination {
  warehouse_id?: number | null;
  code?: string | null;
  name?: string | null;
  kind?: "wholesale" | "warehouse" | string;
  transfer_id?: number | null;
  purpose?: "writeoff" | "finished_goods" | "other" | string | null;
  quantity_kg?: number | null;
  quantity_pieces?: number | null;
}

export interface ForwardDownstream {
  batch_id: number;
  mix_code?: string | null;
  production_lot_code?: string | null;
  product_name?: string | null;
  production_date?: string | null;
  batch_status?: string | null;
  is_semi_finished?: boolean;
  planned_kg?: number | null;
  actual_kg?: number | null;
  dispatches: TraceDispatch[];
}

export interface LotForwardConsumption {
  batch_id: number;
  mix_code?: string | null;
  production_lot_code?: string | null;
  product_name?: string | null;
  production_date?: string | null;
  batch_status?: string | null;
  planned_kg?: number | null;
  actual_kg?: number | null;
  // When the consuming batch is a semi-finished step, its output is consumed by
  // the finished-product batch(es) listed here (so the chain reaches the customer).
  is_semi_finished?: boolean;
  downstream?: ForwardDownstream[];
  dispatches: TraceDispatch[];
}

export interface TraceDocRef {
  id?: number | null;
  number?: string | null;
  code?: string | null;
  date?: string | null;
  production_date?: string | null;
  status?: string | null;
  version?: number | null;
  image_url?: string | null;
  product_name?: string | null;
  customer_name?: string | null;
  supplier_lot?: string | null;
  ingredient?: string | null;
}

export interface ForwardDocuments {
  goods_receipts: TraceDocRef[];
  purchase_orders: TraceDocRef[];
  work_orders: TraceDocRef[];
  recipes: TraceDocRef[];
  dispatch_notes: TraceDocRef[];
}

export interface BackwardDocuments {
  work_order: TraceDocRef | null;
  recipe: TraceDocRef | null;
  goods_receipts: TraceDocRef[];
  purchase_orders: TraceDocRef[];
  dispatch_notes: TraceDocRef[];
}

export interface LotForwardTrace {
  lot: TraceMaterialLot & { expiry_date?: string | null };
  consumptions: LotForwardConsumption[];
  documents?: ForwardDocuments;
}

export interface TraceProducedLot {
  batch_id: number;
  mix_code?: string | null;
  production_lot_code?: string | null;
  product_name?: string | null;
  production_date?: string | null;
  batch_status?: string | null;
}

export interface ProducedByBatch {
  batch_id: number | null;
  mix_code?: string | null;
  production_lot_code?: string | null;
  product_name?: string | null;
  production_date?: string | null;
  batch_status?: string | null;
  is_semi_finished?: boolean;
  semi_finished_id?: number | null;
  inferred?: boolean;
  final_mass_kg?: number | null;
  inputs: BatchInputLot[];
  /// Broj zamjesa koji su proizveli u isti lot kod (lot moze pokrivati cijeli dan).
  /// Kad je >1, `inputs` su zbrojene sirovine svih tih zamjesa.
  batch_count?: number;
  batch_ids?: number[];
}

export interface BatchInputLot {
  lot_id?: number | null;
  ingredient_name?: string | null;
  internal_lot_code?: string | null;
  supplier_lot?: string | null;
  supplier_name?: string | null;
  material_type?: string | null;
  planned_kg?: number | null;
  actual_kg?: number | null;
  // Set when this input lot is itself the output of another (semi-finished) batch.
  is_semi_finished?: boolean;
  produced_by?: ProducedByBatch | null;
  /// Lot je vezan na primku. Kad je false, roba je potrosena bez ulaznog
  /// dokumenta u sustavu (npr. zaliha otvorena prije pocetka uvoza).
  has_receipt?: boolean;
  receipt_id?: number | null;
  receipt_number?: string | null;
  receipt_ids?: number[];
  /// Utrosak je zabiljezen na nalog, a ne na pojedini proizvod, pa je vezan na
  /// sve proizvode naloga bez kolicine.
  is_pooled?: boolean;
  /// Broj sazetih redaka utroska za isti lot.
  line_count?: number;
}

export interface BatchBackwardTrace {
  batch: {
    batch_id: number;
    mix_code?: string | null;
    production_lot_code?: string | null;
    production_lot_assigned_at?: string | null;
    product_name?: string | null;
    production_date?: string | null;
    batch_status?: string | null;
    final_mass_kg?: number | null;
    packed_pieces?: number | null;
    produced_pieces?: number | null;
    scrap_pieces?: number | null;
    scrap_pct?: number | null;
    is_semi_finished?: boolean;
  };
  inputs: BatchInputLot[];
  receipts?: TraceChainReceipt[];
  transfers_in?: TraceChainTransfer[];
  transfers_out?: TraceChainTransfer[];
  qa?: TraceQa;
  product_qa?: TraceProductQa;
  destinations?: TraceDestination[];
  dispatches: TraceDispatch[];
  documents?: BackwardDocuments;
}

// ---- Node detail (sidebar) ----
export interface TraceLotReceipt {
  line_id: number;
  quantity_kg?: number | null;
  price_per_kg?: number | null;
  lot_number?: string | null;
  expiry_date?: string | null;
  receipt?: {
    id: number;
    number?: string | null;
    date?: string | null;
    supplier_name?: string | null;
    image_url?: string | null;
    extra_images?: { url: string; label?: string }[];
  } | null;
  purchase_order?: {
    id: number;
    number?: string | null;
    date?: string | null;
    status?: string | null;
    supplier_name?: string | null;
  } | null;
}

export interface TraceLotUsage {
  batch_id: number;
  mix_code?: string | null;
  production_lot_code?: string | null;
  product_name?: string | null;
  production_date?: string | null;
  planned_kg?: number | null;
  actual_kg?: number | null;
}

export interface TraceLotTransfer {
  line_id: number;
  transfer_id?: number | null;
  number?: string | null;
  date?: string | null;
  from_warehouse?: string | null;
  to_warehouse?: string | null;
  quantity_kg?: number | null;
}

export interface TraceLotNodeDetail {
  kind: "lot";
  lot: {
    lot_id: number;
    internal_lot_code?: string | null;
    supplier_lot?: string | null;
    ingredient_name?: string | null;
    supplier_name?: string | null;
    arrival_date?: string | null;
    expiry_date?: string | null;
    quantity_kg?: number | null;
    status?: string | null;
    notes?: string | null;
  };
  receipts: TraceLotReceipt[];
  /// Premjestaji lota medju skladistima, kronoloski (centralno -> pekara).
  transfers?: TraceLotTransfer[];
  used_in: TraceLotUsage[];
}

export interface TraceRecipeIngredient {
  name: string;
  baker_percentage?: number | null;
  is_child_recipe?: boolean;
  is_semi_finished?: boolean;
  is_adjustable?: boolean;
  notes?: string | null;
}

export interface TraceBatchNodeDetail {
  kind: "batch";
  batch: {
    batch_id: number;
    mix_code?: string | null;
    production_lot_code?: string | null;
    production_lot_assigned_at?: string | null;
    product_name?: string | null;
    production_date?: string | null;
    batch_status?: string | null;
    final_mass_kg?: number | null;
    packed_pieces?: number | null;
    produced_pieces?: number | null;
    scrap_pieces?: number | null;
    scrap_pct?: number | null;
    sequence?: number | null;
    is_semi_finished?: boolean;
  };
  process: {
    mixer_name?: string | null;
    process_name?: string | null;
    oven_name?: string | null;
    mixing_time_min?: number | null;
    bulk_fermentation_min?: number | null;
    processing_time_min?: number | null;
    final_proof_min?: number | null;
    baking_time_min?: number | null;
    mixing_start?: string | null;
    mixing_end?: string | null;
    baking_start?: string | null;
    baking_end?: string | null;
    mixing_workers?: number | null;
    processing_workers?: number | null;
    baking_workers?: number | null;
  };
  work_order: {
    id: number;
    production_date?: string | null;
    version?: number | null;
    status?: string | null;
    confirmed_at?: string | null;
    notes?: string | null;
    /// Oznaka naloga iz izvornog sustava (npr. RN000937). Timeline je dan, a ne
    /// nalog, pa je ovo jedina oznaka samog naloga.
    code?: string | null;
    external_source?: string | null;
    /// Svi proizvodi istog naloga, ukljucujuci trenutni.
    batches?: {
      batch_id: number;
      product_name?: string | null;
      final_mass_kg?: number | null;
      packed_pieces?: number | null;
      is_semi_finished?: boolean;
      is_current?: boolean;
    }[];
    day_batch_count?: number;
    day_order_count?: number;
  } | null;
  phases?: {
    name: string;
    code?: string | null;
    start?: string | null;
    end?: string | null;
    duration_min?: number | null;
    notes?: string | null;
    machine_name?: string | null;
  }[];
  recipe: {
    id: number;
    code?: string | null;
    version?: number | null;
    product_name?: string | null;
    notes?: string | null;
    ingredients: TraceRecipeIngredient[];
  } | null;
  inputs: (BatchInputLot & { lot_id?: number | null; produced_by_batch_id?: number | null })[];
  dispatches: TraceDispatch[];
}

export interface TraceDispatchLine {
  id: number;
  product_name?: string | null;
  lot_code?: string | null;
  mix_code?: string | null;
  quantity?: number | null;
  production_date?: string | null;
}

export interface TraceDispatchNodeDetail {
  kind: "dispatch";
  dispatch: {
    id: number;
    number?: string | null;
    date?: string | null;
    status?: string | null;
    signed_by?: string | null;
    pod_image_url?: string | null;
    notes?: string | null;
    customer?: {
      id: number;
      name?: string | null;
      oib?: number | string | null;
      address?: string | null;
    } | null;
  };
  lines: TraceDispatchLine[];
}

export interface TraceReceiptLine {
  id: number;
  ingredient_name?: string | null;
  lot_number?: string | null;
  internal_lot_code?: string | null;
  quantity_kg?: number | null;
  expiry_date?: string | null;
  notes?: string | null;
}

export interface TraceReceiptNodeDetail {
  kind: "receipt";
  receipt: {
    id: number;
    number?: string | null;
    date?: string | null;
    notes?: string | null;
    image_url?: string | null;
    extra_images?: { url: string; label?: string }[];
    supplier_name?: string | null;
    warehouse?: string | null;
    warehouse_code?: string | null;
    purchase_order?: {
      id: number;
      number?: string | null;
      date?: string | null;
      status?: string | null;
      supplier_name?: string | null;
    } | null;
  };
  lines: TraceReceiptLine[];
}

export interface TraceTransferLine {
  id: number;
  ingredient_name?: string | null;
  lot_number?: string | null;
  internal_lot_code?: string | null;
  quantity_kg?: number | null;
  expiry_date?: string | null;
  transferred?: boolean;
}

export interface TraceTransferNodeDetail {
  kind: "transfer";
  transfer: {
    id: number;
    number?: string | null;
    date?: string | null;
    status?: string | null;
    notes?: string | null;
    from_warehouse?: string | null;
    to_warehouse?: string | null;
    from_code?: string | null;
    to_code?: string | null;
  };
  lines: TraceTransferLine[];
}

export interface TraceQaNodeDetail {
  kind: "qa";
  batch_id: number;
  production_date?: string | null;
  all_passed: boolean;
  items: TraceQaItem[];
}

export interface TraceProductQaNodeDetail {
  kind: "product_qa";
  batch_id: number;
  product_name?: string | null;
  production_lot_code?: string | null;
  mix_code?: string | null;
  production_date?: string | null;
  all_passed: boolean;
  recorded: boolean;
  latest?: FinishedProductCheck | null;
  checks: FinishedProductCheck[];
}

export interface TraceDestinationNodeDetail {
  kind: "destination";
  destination: TraceDestination;
  dispatches: TraceDispatch[];
  transfer?: TraceChainTransfer | null;
}

export type TraceNodeDetail =
  | TraceLotNodeDetail
  | TraceBatchNodeDetail
  | TraceDispatchNodeDetail
  | TraceReceiptNodeDetail
  | TraceTransferNodeDetail
  | TraceQaNodeDetail
  | TraceProductQaNodeDetail
  | TraceDestinationNodeDetail;

// ==================== DISPATCH NOTES (OTPREMNICE) ====================
export interface DispatchCustomer {
  ID: number;
  Name?: string | null;
  OIB?: number | null;
  City?: number | null;
  Address?: string | null;
}

export interface DispatchNoteLine {
  ID?: number;
  DispatchNote?: number | null;
  Product?: number | null;
  RawMaterial?: number | null;
  Quantity?: number | null;
  UnitType?: number | null;
  MaterialLot?: number | null;
  TimelineBatch?: number | null;
  LotCode?: string | null;
  ProductionDate?: string | null;
  TimelineBatch_DispatchNoteLineToTimelineBatch?: {
    ID: number;
    MixCode: string;
    ProductionLotCode?: string | null;
    ProductName?: string | null;
  };
}

export interface DispatchNote {
  ID: number;
  DispatchNumber?: string | null;
  Customer?: number | null;
  Warehouse?: number | null;
  Status?: number | null;
  DispatchDate?: string | null;
  Notes?: string | null;
  Company_DispatchNoteToCompany?: { Name?: string | null } | null;
  DispatchNoteLine?: DispatchNoteLine[];
}

export interface DispatchNoteLinePayload {
  batch_id?: number;
  material_lot_id?: number;
  product_id?: number;
  quantity?: number;
  unit_type?: number;
}

export interface DispatchNotePayload {
  customer_id?: number;
  warehouse_id?: number;
  dispatch_date?: string;
  notes?: string;
  status_id?: number;
  lines: DispatchNoteLinePayload[];
}

// ==================== STAFF ALLOCATION ====================
export interface StaffConfig {
  config_id: number;
  product_id: number;
  machine_id: number;
  workers_default: number;
  valid_from: string;
  valid_to?: string;
  notes?: string;
  created_at?: string;
  zboogie_products?: Product;
  zboogie_machines?: Machine;
}

export interface StaffDaily {
  staff_id: number;
  production_date: string;
  machine_id: number;
  worker_name: string;
  shift_start?: string;
  shift_end?: string;
  notes?: string;
  created_at?: string;
  zboogie_machines?: Machine;
}

// ==================== RECIPE VALIDATION ====================
export interface RecipeMetrics {
  flour_pct: number;
  flour_check: 'ok' | 'warning' | 'error';
  flour_message?: string;
  hydration_1: number;
  hydration_2: number;
  hydration_check: 'ok' | 'warning';
  hydration_message?: string;
  baker_sum: number;
}

// ==================== MATERIAL LOTS (WAREHOUSE) ====================
export interface MaterialLot {
  lot_id: number;
  ingredient_id: number;
  supplier?: string;
  lot_number_supplier?: string;
  internal_lot_code?: string;
  delivery_date: string;
  expiry_date?: string;
  quantity_kg: number;
  remaining_kg: number;
  price_per_kg?: number;
  status: 'pending' | 'released' | 'rejected' | 'depleted';
  material_type?: string;
  notes?: string;
  created_at?: string;
  zboogie_ingredients?: Ingredient;
}

export interface Supplier {
  supplier_id: number;
  supplier_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  is_active?: boolean;
  notes?: string;
  created_at?: string;
}

export interface StockSummary {
  ingredient_id: number;
  ingredient_name: string;
  ingredient_type?: string;
  total_quantity_kg: number;
  total_remaining_kg: number;
  lot_count: number;
  oldest_lot_date?: string;
  newest_lot_date?: string;
}

export interface CreateMaterialLotInput {
  ingredient_id: number;
  supplier?: string;
  lot_number_supplier?: string;
  internal_lot_code?: string;
  delivery_date: string;
  expiry_date?: string;
  quantity_kg: number;
  price_per_kg?: number;
  material_type?: string;
  notes?: string;
}

// ==================== STAFF & SHIFTS ====================
export interface Staff {
  staff_id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  employee_code?: string;
  department?: string;
  position?: string;
  email?: string;
  phone?: string;
  hire_date?: string;
  is_active?: boolean;
  notes?: string;
  created_at?: string;
  zboogie_staff_skills?: StaffSkill[];
  zboogie_shift_assignments?: ShiftAssignment[];
}

export interface StaffSkill {
  skill_id: number;
  staff_id: number;
  skill_name: string;
  proficiency_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  certified?: boolean;
  certification_date?: string;
  notes?: string;
  created_at?: string;
}

export interface Shift {
  shift_id: number;
  shift_name: string;
  shift_code?: string;
  start_time: string;
  end_time: string;
  is_active?: boolean;
  notes?: string;
  created_at?: string;
}

export interface ShiftAssignment {
  assignment_id: number;
  staff_id: number;
  shift_id: number;
  machine_id?: number;
  assignment_date: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
  created_at?: string;
  zboogie_staff?: Staff;
  zboogie_shifts?: Shift;
  zboogie_machines?: Machine;
}

export interface CreateStaffInput {
  first_name: string;
  last_name: string;
  employee_code?: string;
  department?: string;
  position?: string;
  email?: string;
  phone?: string;
  hire_date?: string;
  notes?: string;
}

export interface CreateShiftAssignmentInput {
  staff_id: number;
  shift_id: number;
  machine_id?: number;
  assignment_date: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
}

// ==================== REPORTS (IZVJEŠTAJI) ====================
// Dnevni financijsko-operativni izvještaj. Payload dolazi iz agregata na
// backendu pa je, kao i ostali agregati, snake_case.

export interface ReportTimelineInfo {
  id: number;
  version: number;
  status: string;
  /** "YYYY-MM-DD HH:MM:SS" bez vremenske zone. */
  confirmed_at: string | null;
}

export interface ReportPlannedBatch {
  batch_id: number;
  sequence: number;
  mix_code: string;
  product_name: string;
  product_id: number | null;
  semi_finished_id: number | null;
  is_semi_finished: boolean;
  final_mass_kg: number;
  planned_pieces: number;
  vpc_unit_price: number;
  planned_vpc_total: number;
  total_ingredient_cost: number;
  cost_per_kg: number;
  recipe_version: number | null;
  mixer: string;
  process: string;
  oven: string;
  /** Satnice su "HH:MM" tekst iz baze (bez pomaka zone). */
  mixing_start: string | null;
  mixing_end: string | null;
  processing_start: string | null;
  processing_end: string | null;
  baking_start: string | null;
  baking_end: string | null;
}

export interface ReportRealizedBatch {
  id: number;
  mix_code: string;
  product_id: number | null;
  product_name: string;
  dough_mass_kg: number;
  produced_pieces: number;
  packed_pieces: number;
  scrap_pieces: number;
  scrap_pct: number;
  vpc_unit_price: number;
  realized_vpc_total: number;
  mixer: string;
  process: string;
  oven: string;
  mixing_start: string | null;
  mixing_end: string | null;
  baking_start: string | null;
  baking_end: string | null;
  timeline_batch_id: number | null;
}

export interface ReportPlanGroup {
  product_name: string;
  is_semi_finished: boolean;
  batches_cnt: number;
  total_mass_kg: number;
  total_pieces: number;
  total_ingredient_cost: number;
  cost_per_piece: number;
  vpc_unit_price: number;
  total_vpc_value: number;
  margin_eur: number;
}

export interface ReportRealizedGroup {
  product_name: string;
  batches_cnt: number;
  total_mass_kg: number;
  produced_pieces: number;
  packed_pieces: number;
  scrap_pieces: number;
  scrap_pct: number;
  vpc_unit_price: number;
  total_vpc_value: number;
  actual_ingredient_cost: number;
  margin_eur: number;
}

export interface ReportComparisonRow {
  product_name: string;
  plan_batches: number;
  real_batches: number;
  plan_pieces: number;
  real_pieces: number;
  diff_pieces: number;
  scrap_pieces: number;
  /** null kada nema evidentiranih komada — izvještaj tada prikazuje "-". */
  scrap_pct: number | null;
  yield_pct: number | null;
  plan_vpc: number;
  real_vpc: number;
  diff_vpc: number;
  plan_cost: number;
  real_cost: number;
  diff_cost: number;
}

export interface ReportMachineSegment {
  /** Minute od ponoći. */
  start_min: number;
  end_min: number;
  label: string;
}

export interface ReportMachineLane {
  machine: string;
  /** Mikser | Obrada | Peć */
  type: string;
  plan: ReportMachineSegment[];
  actual: ReportMachineSegment[];
}

export interface ReportMachineTimeline {
  lanes: ReportMachineLane[];
  min_minutes: number;
  max_minutes: number;
}

export interface ReportPlanTotals {
  batches_cnt: number;
  mass_kg: number;
  pieces: number;
  ingredient_cost: number;
  vpc_value: number;
  margin: number;
  margin_pct: number;
}

export interface ReportRealTotals {
  batches_cnt: number;
  mass_kg: number;
  produced_pieces: number;
  packed_pieces: number;
  scrap_pieces: number;
  scrap_pct: number;
  ingredient_cost: number;
  vpc_value: number;
  margin: number;
  margin_pct: number;
  yield_pct: number;
}

export interface DailyProductionReport {
  target_date: string;
  generated_at: string;
  final_timeline: ReportTimelineInfo | null;
  /** Najraniji zamjes tog dana, "YYYY-MM-DD HH:MM:SS". */
  prod_start: string | null;
  timeline_versions_cnt: number;
  changes_before_start: number;
  changes_during_prod: number;
  planned_batches: ReportPlannedBatch[];
  realized_batches: ReportRealizedBatch[];
  plan_grouped: ReportPlanGroup[];
  realized_grouped: ReportRealizedGroup[];
  comparison: ReportComparisonRow[];
  plan_totals: ReportPlanTotals;
  real_totals: ReportRealTotals;
  machine_timeline: ReportMachineTimeline;
}
